/**
 * SEOHead — reusable per-page <head> manager.
 *
 * Sets <title>, meta description/keywords, canonical, OpenGraph + Twitter
 * cards, and (optionally) injects JSON-LD structured data.
 *
 * Every page should render <SEOHead {...} /> as its first child so Google
 * indexes per-URL metadata instead of inheriting the site-wide defaults.
 */
import { Helmet } from "react-helmet-async";

const SITE = "https://www.plangeneratorca.com";

const SEOHead = ({
  title,
  description,
  keywords,
  path = "/",
  image = `${SITE}/og-image.png`,
  jsonLd = null,
}) => {
  const fullTitle = title.includes("Plan Generator CA")
    ? title
    : `${title} | Plan Generator CA`;
  const url = `${SITE}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Plan Generator CA" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Optional structured data */}
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
    </Helmet>
  );
};

export default SEOHead;
